// Word-navigation mode (R-keyboard) — the DOM/React half of the token cursor.
// The gloss popover is reachable by mouse and touch today, but a keyboard
// reader has no way to select a token at all: `SelectionLayer` only ever
// captures a `window.getSelection()` a pointer produced. This hook is the
// missing input method.
//
// The design in one sentence: the cursor is a MODEL-level token span
// (`./token-cursor`, pure and tested), and its visible representation is a REAL
// DOM `Selection` painted from that span. So the keyboard path re-enters
// `normalizeSelection` through exactly the same door a mouse drag does, with
// exactly the same `[start, end)` — one normalizer, one cache key, two ways in.
// Nothing here may fork the capture logic; the only thing it does is produce a
// selection and then ask the layer to capture it (`onInvoke`).
//
// Everything impure lives here (functional-programming policy §9): all the
// arithmetic — which token is next, what does the span cover, is it at an edge
// — is `./token-cursor`'s, and this file only reads the DOM, paints, scrolls,
// focuses and announces.
//
// KEYBOARD INPUT GENERATES NO POINTER EVENTS. That is the load-bearing fact
// behind this hook sitting alongside, and never inside, the mouse/touch state
// machine in `selection-layer.tsx` — see the note there.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import type { DocumentModel } from "@bindery/shared/corpus/document";
import {
  offsetAtPoint,
  offsetToPoint,
  ORIGINAL_SCOPE,
  selectionToOffsets,
  selectOffsets,
} from "./dom-offsets";
import {
  cursorSpan,
  describeCursor,
  firstTokenInView,
  moveCursor,
  type CursorBounds,
  type CursorOp,
  type TokenCursor,
} from "./token-cursor";

/**
 * The elements a reader may press Enter on to start word navigation. Supplied
 * by the renderers, not by this hook: the original `DocumentPane`'s container
 * on desktop, each OPEN inset's content on mobile. A collapsed inset is
 * deliberately not one — its trigger is already keyboard-reachable and
 * expanding it is the way in.
 */
const ENTRY = "[data-gloss-entry]";

/** Keeps the cursor this far from the scroll container's edges when it moves. */
const SCROLL_MARGIN = 32;

/** Which navigation op a key means, or `null` if this hook doesn't own the key. */
function keyOp(e: KeyboardEvent): CursorOp | null {
  if (e.altKey || e.ctrlKey || e.metaKey) return null;
  switch (e.key) {
    case "ArrowRight":
      return e.shiftKey ? "extendRight" : "nextWord";
    case "ArrowLeft":
      return e.shiftKey ? "extendLeft" : "prevWord";
    case "ArrowDown":
      return e.shiftKey ? null : "nextBlock";
    case "ArrowUp":
      return e.shiftKey ? null : "prevBlock";
    case "Home":
      return e.shiftKey ? null : "blockStart";
    case "End":
      return e.shiftKey ? null : "blockEnd";
    default:
      return null;
  }
}

/** Does this op run toward the start of the text? (Which edge did we hit.) */
function movesBackward(op: CursorOp): boolean {
  return op === "prevWord" || op === "extendLeft" || op === "prevBlock";
}

/**
 * Char-offset extent of an entry element, read from the `[data-start]` runs it
 * contains — the whole original on desktop, one inset's stretch on mobile.
 * `null` when the element holds no resolvable run.
 *
 * Blocks carry `data-start` as well as the emphasis runs nested inside them
 * (`document-blocks.tsx`), so this deliberately takes the min/max over all of
 * them rather than trying to pick a level: nesting can only ever agree.
 */
function boundsOf(entry: HTMLElement): CursorBounds | null {
  let start = Number.POSITIVE_INFINITY;
  let end = -1;
  for (const run of entry.querySelectorAll<HTMLElement>("[data-start]")) {
    const base = Number(run.dataset.start);
    if (Number.isNaN(base)) continue;
    const stop = base + (run.textContent?.length ?? 0);
    if (base < start) start = base;
    if (stop > end) end = stop;
  }
  return end < start ? null : { start, end };
}

/**
 * A model offset near the top of whatever part of `entry` is on screen — the
 * seed for `firstTokenInView`, so entering the mode lands the reader on the
 * word they are looking at rather than back at the top of the chapter.
 *
 * Probes downward with `offsetAtPoint` instead of measuring every run: the
 * topmost pixel row very often falls in a margin between blocks where no caret
 * resolves, and a chapter can hold thousands of runs. Falls back to the start
 * of `bounds`, which is always a legal answer.
 */
function seedOffset(
  root: HTMLElement,
  entry: HTMLElement,
  bounds: CursorBounds,
): number {
  const view = root.getBoundingClientRect();
  const box = entry.getBoundingClientRect();
  const top = Math.max(view.top, box.top);
  const bottom = Math.min(view.bottom, box.bottom);
  if (bottom <= top) return bounds.start;
  const x = box.left + box.width / 2;
  for (let y = top + 4; y < Math.min(bottom, top + 240); y += 12) {
    const offset = offsetAtPoint(root, x, y, ORIGINAL_SCOPE);
    if (offset !== null && offset >= bounds.start && offset <= bounds.end) {
      return offset;
    }
  }
  return bounds.start;
}

export function useGlossKeyboard({
  rootRef,
  model,
  onInvoke,
  popoverOpen,
  sinkRef,
  announceRef,
}: {
  /** The scroll container holding the original's runs (see `SelectionLayer`). */
  rootRef: RefObject<HTMLDivElement | null>;
  /** The ORIGINAL document — the same model the layer normalizes against. */
  model: DocumentModel;
  /**
   * Enter in the mode: the layer runs its shared capture with `via:
   * "keyboard"` and calls back with whether it actually took one. The capture
   * is rAF-deferred, so this cannot be a return value.
   */
  onInvoke: (done: (captured: boolean) => void) => void;
  /** Navigation keys are ignored while the popover owns the keyboard. */
  popoverOpen: boolean;
  /** The `sr-only` focus sink the mode parks focus on while navigating. */
  sinkRef: RefObject<HTMLElement | null>;
  /** The polite live region announcements are written into. */
  announceRef: RefObject<HTMLElement | null>;
}): {
  modeActive: boolean;
  restore: () => void;
  /**
   * Leave the mode. `refocus` hands focus back to the entry region, which is
   * right for a DELIBERATE exit (Escape) and wrong for one the reader caused
   * by moving focus or the pointer elsewhere — see `exitMode`.
   */
  exit: (refocus: boolean) => void;
} {
  const [modeActive, setModeActive] = useState(false);

  // Mirrors for the document-level listener, whose effect deliberately doesn't
  // re-run when these change — the same pattern `SelectionLayer` uses for its
  // own logger/capture mirrors.
  const onInvokeRef = useRef(onInvoke);
  const popoverOpenRef = useRef(popoverOpen);
  useEffect(() => {
    onInvokeRef.current = onInvoke;
    popoverOpenRef.current = popoverOpen;
  });

  // `restore`/`exit` are called from the popover's Radix handlers, which are
  // recreated every render; routing them through a ref keeps the returned
  // callbacks referentially stable and keeps the mode's whole implementation
  // inside the one effect that owns its state.
  const apiRef = useRef<{
    restore: () => void;
    exit: (refocus: boolean) => void;
  }>({
    restore: () => undefined,
    exit: () => undefined,
  });

  useEffect(() => {
    // Read `rootRef.current` live in each handler (not captured here) so a
    // desktop-pane ↔ mobile-column swap, which reuses the same ref for a new
    // node, keeps working without re-running this effect.
    const sink = sinkRef.current;

    /**
     * The whole of the mode's state. `null` means "not in the mode", which is
     * why every handler below starts by asking for it: there is exactly one
     * place that can be in an inconsistent state, and it is this variable.
     */
    let session: {
      entry: HTMLElement;
      bounds: CursorBounds;
      cursor: TokenCursor;
    } | null = null;

    /**
     * True between invoking a capture and the popover handing focus back.
     * Focus leaving the sink normally ends the mode, but Radix focusing the
     * popover content is exactly that, and it cannot be told apart by reading
     * `popoverOpenRef`: React runs the child (Radix) focus effect BEFORE the
     * parent effect that would have updated that mirror, so the blur arrives
     * while the mirror still says "closed".
     */
    let handedOff = false;

    /**
     * Written imperatively — announcing through React state would re-render the
     * whole layer on every arrow key. A screen reader only speaks a live region
     * when its text CHANGES, so two identical consecutive announcements (the
     * same word twice in one line) would be silent; the padding space makes the
     * text differ without changing what is spoken.
     */
    let lastAnnounced = "";
    const announce = (message: string): void => {
      const node = announceRef.current;
      if (!node) return;
      const text = message === lastAnnounced ? `${message} ` : message;
      lastAnnounced = text;
      node.textContent = text;
    };

    const describe = (cursor: TokenCursor): string => {
      const { quote, blockOrdinal, tokenCount } = describeCursor(model, cursor);
      const where = `line ${String(blockOrdinal)}`;
      return tokenCount > 1
        ? `${quote}, ${String(tokenCount)} words, ${where}`
        : `${quote}, ${where}`;
    };

    /**
     * Paint a cursor as a real DOM selection, and CONFIRM it reads back as the
     * same thing. The read-back is not paranoia: `selectOffsets` locates its two
     * endpoints run by run, so on the mobile interleaved column a span whose
     * ends fall in two different insets paints happily across the retold prose
     * between them — and `selectionToOffsets`' same-scope test is the check that
     * catches it. A caller that ignored this would hand `normalizeSelection` a
     * span covering text the reader never selected.
     */
    const paint = (root: HTMLElement, cursor: TokenCursor): boolean => {
      const span = cursorSpan(model, cursor);
      if (!selectOffsets(root, span.start, span.end, ORIGINAL_SCOPE)) {
        return false;
      }
      const sel = window.getSelection();
      return selectionToOffsets(root, sel, ORIGINAL_SCOPE) !== null;
    };

    /**
     * Bring the cursor's HEAD token into view. Instant by construction — a
     * smooth scroll still in flight when Enter fires would emit trailing scroll
     * events into the layer's capture-phase dismissal listener and close the
     * popover that just opened.
     *
     * Scrolls to the head token's own rect rather than calling `scrollIntoView`
     * on its run: a run is a whole block, and `block: "nearest"` on a paragraph
     * taller than the viewport does nothing at all while the head sits offscreen
     * inside it.
     */
    const revealHead = (root: HTMLElement, cursor: TokenCursor): void => {
      const token = model.tokens[cursor.head];
      if (!token) return;
      const a = offsetToPoint(root, token.start, ORIGINAL_SCOPE);
      const b = offsetToPoint(root, token.end, ORIGINAL_SCOPE);
      if (!a || !b) return;
      const range = document.createRange();
      range.setStart(a.node, a.offset);
      range.setEnd(b.node, b.offset);
      const rect = range.getBoundingClientRect();
      if (rect.height === 0) return;
      const view = root.getBoundingClientRect();
      if (rect.top < view.top + SCROLL_MARGIN) {
        root.scrollTop -= view.top + SCROLL_MARGIN - rect.top;
      } else if (rect.bottom > view.bottom - SCROLL_MARGIN) {
        root.scrollTop += rect.bottom - (view.bottom - SCROLL_MARGIN);
      }
    };

    const exitMode = (refocus: boolean): void => {
      const entry = session?.entry ?? null;
      session = null;
      handedOff = false;
      setModeActive(false);
      window.getSelection()?.removeAllRanges();
      // Only on a DELIBERATE exit (Escape). Blur-driven exits must not fight
      // the Tab that caused them by yanking focus back.
      if (refocus && entry?.isConnected) entry.focus({ preventScroll: true });
      announce("");
      lastAnnounced = "";
    };

    const restore = (): void => {
      const root = rootRef.current;
      handedOff = false;
      if (!session || !root) return;
      paint(root, session.cursor);
      sinkRef.current?.focus({ preventScroll: true });
      announce(describe(session.cursor));
    };

    const enter = (root: HTMLElement, entry: HTMLElement): void => {
      const bounds = boundsOf(entry);
      if (!bounds) return;
      const seed =
        firstTokenInView(model, seedOffset(root, entry, bounds), bounds) ??
        firstTokenInView(model, bounds.start, bounds);
      if (seed === null) return;
      const cursor: TokenCursor = { anchor: seed, head: seed };
      if (!paint(root, cursor)) return;
      session = { entry, bounds, cursor };
      setModeActive(true);
      sinkRef.current?.focus({ preventScroll: true });
      revealHead(root, cursor);
      announce(
        `Word navigation. ${describe(cursor)}. ` +
          `Left and right arrows move by word, up and down by line, ` +
          `shift with an arrow extends the selection, ` +
          `Enter translates, Escape exits.`,
      );
    };

    const navigate = (root: HTMLElement, op: CursorOp): void => {
      if (!session) return;
      const { cursor, atEdge } = moveCursor(
        model,
        session.cursor,
        op,
        session.bounds,
      );
      if (atEdge) {
        announce(movesBackward(op) ? "Start of passage." : "End of passage.");
        return;
      }
      if (!paint(root, cursor)) {
        // Roll back to the span that was on screen a moment ago: a failed paint
        // has already disturbed the live selection, so leaving it would strand
        // the reader on a selection that no longer matches the cursor.
        paint(root, session.cursor);
        announce("A selection can’t cross passages.");
        return;
      }
      session.cursor = cursor;
      revealHead(root, cursor);
      announce(describe(cursor));
    };

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.defaultPrevented) return;
      const root = rootRef.current;
      if (!root) return;

      if (session === null) {
        // Enter on an entry region starts the mode. Tested on the target
        // ITSELF, not an ancestor: the desktop entry is the whole original
        // column, and a page-turn link inside it must keep its own Enter.
        if (e.key !== "Enter" || e.altKey || e.ctrlKey || e.metaKey) return;
        const target = e.target;
        if (!(target instanceof HTMLElement) || !target.matches(ENTRY)) return;
        if (!root.contains(target)) return;
        e.preventDefault();
        enter(root, target);
        return;
      }

      // The entry region can go out from under the mode without any event of
      // its own — a chapter turn, a collapsing inset, a layout swap. This is
      // NOT the blur-driven exit `exitMode(false)` exists for: no blur
      // happened, so focus is still sitting on the sink — an `sr-only`
      // `role="application"` span with nothing behind it, where every later
      // key does nothing and the only way out is to Tab off an element the
      // reader can't see. Hand focus to the pane; if the pane is not a tab
      // stop (the mobile interleaved column isn't), let focus go entirely
      // rather than strand them there.
      if (!session.entry.isConnected) {
        exitMode(false);
        root.focus({ preventScroll: true });
        if (document.activeElement === sinkRef.current) {
          sinkRef.current?.blur();
        }
        return;
      }

      if (e.key === "Escape") {
        // While the popover is open, Escape is ITS dismissal; the mode gets
        // the cursor and the sink back through the layer's close path
        // (`onOpenChange` → `restore()`).
        if (popoverOpenRef.current) return;
        e.preventDefault();
        exitMode(true);
        return;
      }

      // Everything below is only the mode's while the sink actually holds
      // focus. This is what keeps Enter and the arrows out of the popover's
      // own keyboard handling once Radix has moved focus into it.
      if (
        document.activeElement !== sinkRef.current ||
        popoverOpenRef.current
      ) {
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        handedOff = true;
        onInvokeRef.current((captured) => {
          // A capture can resolve to nothing (a selection that no longer
          // normalizes, a root that went away): no popover opens, so the sink
          // is never blurred and `handedOff` would stay true forever —
          // `onSinkBlur` would then ignore every later blur, leaving a mode
          // that cannot be exited by Tab and a caret-browsing capture
          // suppressed for the rest of the chapter, with no timeout to
          // recover. The layer reports back so the hand-off can be taken back.
          if (!captured) handedOff = false;
        });
        return;
      }

      const op = keyOp(e);
      if (op === null) return;
      e.preventDefault();
      handedOff = false;
      navigate(root, op);
    };

    const onSinkBlur = (): void => {
      if (handedOff || popoverOpenRef.current) return;
      exitMode(false);
    };

    apiRef.current = { restore, exit: exitMode };

    document.addEventListener("keydown", onKeyDown);
    sink?.addEventListener("blur", onSinkBlur);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      sink?.removeEventListener("blur", onSinkBlur);
      apiRef.current = { restore: () => undefined, exit: () => undefined };
      // A chapter turn or a layout swap re-runs this effect with a fresh,
      // empty `session`; the React-visible flag has to follow it down.
      setModeActive(false);
    };
  }, [model, rootRef, sinkRef, announceRef]);

  const restore = useCallback(() => {
    apiRef.current.restore();
  }, []);
  const exit = useCallback((refocus: boolean) => {
    apiRef.current.exit(refocus);
  }, []);

  return { modeActive, restore, exit };
}
